"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { createPunchoutUserAction, deletePunchoutUserAction } from "@/actions/ociSettingsActions"
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons"
import { ArrowDownAZ, ArrowUpAZ, LoaderCircle, PlusIcon, SaveIcon, SearchIcon, TrashIcon, XIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { PunchoutUser } from "@/lib/storefront/account/oci-service"
import {
  Button,
  FormItem,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui/components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/components/alert-dialog"
import { PasswordToggleInput } from "@/components/ui/components/password-toggle-input"
import { cn } from "@/components/ui/helpers/utils"
import { FormRow } from "@/components/account/shared/FormRow"
import PasswordChecklist from "@/components/common/PasswordChecklist"

import GlobalMessageBanner from "./GlobalMessageBanner"

interface AccountOciPunchoutClientProps {
  users: PunchoutUser[]
  labelTitle: string
  labelSubtitle: string
  labelBtnConfig: string
  labelUserListTitle: string
  labelPlaceholderUserListSearch: string
  labelBtnUserAdd: string
  labelBtnUserAddClose: string
  labelErrorUserAdd: string
  labelUserType: string
  labelTypeOCI: string
  labelTypeCXML: string
  labelUserName: string
  labelPlaceholderUserName: string
  labelErrorUserNameMissing: string
  labelUserPassword: string
  labelPlaceholderUserPassword: string
  labelErrorUserPasswordMissing: string
  password_checklist_minlength?: string
  password_checklist_lowercase?: string
  password_checklist_capital?: string
  password_checklist_specialchar?: string
  password_checklist_number?: string
  labelBtnReset: string
  labelBtnDelete: string
  labelBtnSave: string
  labelPunchoutUrlsTitle: string
  labelPunchoutUrlsDescription: string
  labelUrlTitleOCI: string
  labelUrlTitleCXML: string
  urlPunchoutConfigOCI: string
  urlPunchoutConfigCXML: string
  linkPunchoutConfig: string
  alertDeleteUserTitle: string
  alertDeleteUserDescription: string
}

export default function AccountOciPunchoutClient(props: AccountOciPunchoutClientProps) {
  const t = useTranslations("storefront")
  const [searchTerm, setSearchTerm] = React.useState("")
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">("asc")
  const [allUsers, setAllUsers] = React.useState<PunchoutUser[]>(props.users)
  const currentUsers = React.useMemo(
    () =>
      allUsers
        .filter((u) => u.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => (sortDirection == "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name))),
    [searchTerm, allUsers, sortDirection]
  )
  const [showAddUser, setShowAddUser] = React.useState<boolean>(false)
  const [globalMessage, setGlobalMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null)
  const globalMessageRef = React.useRef<HTMLDivElement | null>(null)

  const loading = false

  const [password, setPassword] = useState("")
  const [name, setName] = useState("")
  const [type, setType] = useState("OCI")
  const [nameError, setNameError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const resetAddUserForm = () => {
    setName("")
    setType("OCI")
    setPassword("")
    setGlobalMessage(null)
    setNameError(null)
    setPasswordError(null)
  }

  const addUserAction = (formData: FormData) => {
    createPunchoutUserAction(formData)
      .then((user: PunchoutUser) => {
        setAllUsers([user, ...allUsers])
        resetAddUserForm()
        setShowAddUser(false)
      })
      .catch((err) => {
        console.error(err)
        setGlobalMessage({ type: "error", text: props.labelErrorUserAdd ?? "Fehler beim Anlegen des Benutzers" })
        setTimeout(() => {
          if (globalMessageRef.current) {
            globalMessageRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 50)
      })
      .finally(() => setPassword(""))
  }
  const removeUserAction = (userName?: string) => {
    deletePunchoutUserAction(userName)
      .then(() => {
        setAllUsers(allUsers.filter((u) => u.name !== userName))
      })
      .catch((err) => {
        console.log(err)
      })
  }
  const handleAddUserSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!name) {
      e.preventDefault()
      setNameError(props.labelErrorUserNameMissing ?? "Bitte geben Sie einen Anmeldenamen an.")
      const el = e.currentTarget.querySelector('input[name="name"]') as HTMLElement | null
      if (el) el.focus()
      return
    }
    if (!password) {
      e.preventDefault()
      setPasswordError(props.labelErrorUserPasswordMissing ?? "Bitte geben Sie ein gültiges Kennwort ein.")
      const el = e.currentTarget.querySelector('input[name="password"]') as HTMLElement | null
      if (el) el.focus()
      return
    }
  }
  const loginTypes = [
    { label: props.labelTypeOCI, value: "OCI" },
    { label: props.labelTypeCXML, value: "cXML" },
  ]
  const [punchoutConfigDomain, setPunchoutConfigDomain] = React.useState<string>("")
  const [punchoutConfigUser, setPunchoutConfigUser] = React.useState<string>("")
  const [punchoutConfigPass, setPunchoutConfigPass] = React.useState<string>("")
  const [punchoutConfigHook, setPunchoutConfigHook] = React.useState<string>("")
  const punchoutConfigUrls = React.useMemo(
    () => [
      {
        label: props.labelUrlTitleOCI,
        value: String(props.urlPunchoutConfigOCI)
          .replace("<DOMAIN>", punchoutConfigDomain == "" ? "<DOMAIN>" : punchoutConfigDomain)
          .replace("<USERNAME>", punchoutConfigUser == "" ? "<USERNAME>" : punchoutConfigUser)
          .replace("<PASSWORD>", punchoutConfigPass == "" ? "<PASSWORD>" : punchoutConfigPass)
          .replace("<HOOK_URL>", punchoutConfigHook == "" ? "<HOOK_URL>" : punchoutConfigHook),
      },
      {
        label: props.labelUrlTitleCXML,
        value: String(props.urlPunchoutConfigCXML)
          .replace("<DOMAIN>", punchoutConfigDomain == "" ? "<DOMAIN>" : punchoutConfigDomain)
          .replace("<USERNAME>", punchoutConfigUser == "" ? "<USERNAME>" : punchoutConfigUser)
          .replace("<PASSWORD>", punchoutConfigPass == "" ? "<PASSWORD>" : punchoutConfigPass)
          .replace("<HOOK_URL>", punchoutConfigHook == "" ? "<HOOK_URL>" : punchoutConfigHook),
      },
    ],
    [
      props.labelUrlTitleOCI,
      props.urlPunchoutConfigOCI,
      props.labelUrlTitleCXML,
      props.urlPunchoutConfigCXML,
      punchoutConfigDomain,
      punchoutConfigUser,
      punchoutConfigPass,
      punchoutConfigHook,
    ]
  )

  useEffect(
    () => {
      if (globalMessage) {
        setGlobalMessage(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.users]
  )

  useEffect(() => {
    if (process) {
      setPunchoutConfigDomain(new URL(window.location.href).hostname)
    }
  }, [])

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        {/* Title / Button Config */}
        <div className={"flex flex-row justify-between gap-2"}>
          <div>
            <span className={"text-2xl font-semibold"}>{props.labelTitle}</span>
            <p>{props.labelSubtitle}</p>
          </div>
          <Button size={"defaultNoGrow"} asChild={true}>
            <Link href={props.linkPunchoutConfig} prefetch={false}>
              {props.labelBtnConfig}
            </Link>
          </Button>
        </div>
        {/* Punchout User List and Create Form */}
        <div className={"flex flex-col gap-2"}>
          {/* User List Table */}
          <div className={"flex flex-col gap-4"}>
            <h2 className={"-mb-2 text-xl font-semibold"}>{props.labelUserListTitle}</h2>
            <div
              className={
                "flex flex-row flex-wrap justify-between gap-2 rounded-sm border border-gray-300 bg-gray-200 p-2"
              }
            >
              <div className={"flex flex-row gap-2 max-lg:w-full max-lg:justify-between lg:w-fit"}>
                <div className="relative h-fit w-full lg:w-80">
                  {!loading && searchTerm != "" ? (
                    <button
                      onClick={() => setSearchTerm("")}
                      className={"absolute top-1/2 right-2 size-5 -translate-y-1/2 text-gray-400"}
                    >
                      <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
                    </button>
                  ) : (
                    <div
                      className={"pointer-events-none absolute top-1/2 right-2 size-5 -translate-y-1/2 text-gray-400"}
                    >
                      {loading ? (
                        <LoaderCircle className={"size-5 motion-safe:animate-spin"} />
                      ) : (
                        <SearchIcon strokeWidth={2} className="size-5" />
                      )}
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder={props.labelPlaceholderUserListSearch}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={loading}
                    className="
                      w-full rounded-lg border border-gray-300 py-2 pr-10 pl-4 text-sm text-gray-700
                      placeholder:text-gray-400
                      focus:ring-2 focus:ring-blue-500 focus:outline-hidden
                    "
                  />
                </div>
                <Button
                  size={"iconNoGrow"}
                  type={"button"}
                  variant={"ghost"}
                  onClick={() => {
                    setSortDirection((prevState) => (prevState === "asc" ? "desc" : "asc"))
                  }}
                >
                  {sortDirection === "asc" ? (
                    <ArrowDownAZ className={"size-5 object-contain"} />
                  ) : (
                    <ArrowUpAZ className={"size-5 object-contain"} />
                  )}
                </Button>
              </div>
              <Button
                size={"defaultNoGrow"}
                variant={showAddUser ? "outline_secondary" : "default"}
                type={"button"}
                className={"gap-1"}
                disabled={loading}
                onClick={() => {
                  setShowAddUser((p) => {
                    const next = !p
                    if (!next) {
                      resetAddUserForm()
                    }
                    return next
                  })
                }}
              >
                {loading ? (
                  <LoaderCircle className={"size-5 object-contain motion-safe:animate-spin"} />
                ) : showAddUser ? (
                  <XIcon className={"size-5 object-contain"} />
                ) : (
                  <PlusIcon className={"size-5 object-contain"} />
                )}
                {showAddUser ? props.labelBtnUserAddClose : props.labelBtnUserAdd}
              </Button>
            </div>
            {/* USER ADD FORM */}
            {showAddUser && (
              <div className={"flex flex-col gap-2 rounded-sm border border-gray-300 bg-gray-200 p-2"}>
                <GlobalMessageBanner
                  message={globalMessage}
                  refEl={globalMessageRef}
                  onCloseAction={() => setGlobalMessage(null)}
                />
                <form action={addUserAction} onSubmit={handleAddUserSubmit} className={"flex-row gap-1 py-2"}>
                  <FormRow label={props.labelUserType} htmlFor={"type"}>
                    <FormItem>
                      <Select name={"type"} value={type} onValueChange={(v: string) => setType(v)}>
                        <SelectTrigger className="my-auto h-8 w-full rounded-lg">
                          <SelectValue placeholder="" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {loginTypes.map(({ label, value }) => (
                              <SelectItem value={value} key={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  </FormRow>
                  <FormRow label={props.labelUserName} htmlFor={"name"}>
                    <FormItem>
                      <Input
                        type={"text"}
                        placeholder={props.labelPlaceholderUserName}
                        name={"name"}
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value)
                          if (e.target.value) setNameError(null)
                        }}
                      />
                      {nameError && <p className="text-sm text-red-500">{nameError}</p>}
                    </FormItem>
                  </FormRow>
                  <FormRow label={props.labelUserPassword} htmlFor={"password"}>
                    <FormItem>
                      <PasswordToggleInput
                        autoComplete={"new-password"}
                        placeholder={props.labelPlaceholderUserPassword}
                        name={"password"}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          if (e.target.value) setPasswordError(null)
                        }}
                      />
                      {passwordError && <p className="text-sm text-red-500">{passwordError}</p>}

                      <PasswordChecklist
                        className={"mt-1 flex flex-wrap items-center justify-start gap-2 text-xs"}
                        itemClassName={"flex gap-1 items-center pt-0"}
                        rules={["minLength", "lowercase", "capital", "specialChar", "number"]}
                        value={password}
                        minLength={10}
                        messages={{
                          minLength: props.password_checklist_minlength,
                          lowercase: props.password_checklist_lowercase,
                          capital: props.password_checklist_capital,
                          specialChar: props.password_checklist_specialchar,
                          number: props.password_checklist_number,
                        }}
                        iconSize={16}
                        iconComponents={{
                          ValidIcon: (
                            <CheckCircledIcon
                              className={cn({ "text-gray-400": !password, "text-green-500": password })}
                            />
                          ),
                          InvalidIcon: (
                            <CrossCircledIcon
                              className={cn({ "text-gray-400": !password, "text-red-500": password })}
                            />
                          ),
                        }}
                      />
                    </FormItem>
                  </FormRow>
                  <div className="flex justify-between gap-2 pt-2 max-lg:flex-col-reverse lg:flex-row">
                    <Button
                      size={"defaultNoGrow"}
                      variant={"outline"}
                      type={"reset"}
                      disabled={loading}
                      className={"gap-1"}
                      onClick={() => {
                        resetAddUserForm()
                      }}
                    >
                      <XIcon className={"size-5 object-contain"} />
                      {props.labelBtnReset}
                    </Button>
                    <Button
                      size={"defaultNoGrow"}
                      variant={"default"}
                      type={"submit"}
                      disabled={loading}
                      className={"gap-1"}
                    >
                      {loading ? (
                        <LoaderCircle className={"size-5 object-contain motion-safe:animate-spin"} />
                      ) : (
                        <SaveIcon className={"size-5 object-contain"} />
                      )}
                      {props.labelBtnSave}
                    </Button>
                  </div>
                </form>
              </div>
            )}
            {/* USER LIST */}
            <div className={"flex flex-col gap-1"}>
              {currentUsers.map((user) => (
                <div
                  key={user.name}
                  className={"flex flex-col gap-0 rounded-sm border border-gray-200 bg-white px-2 hover:bg-gray-100"}
                >
                  <div className={"group flex cursor-pointer flex-row flex-nowrap justify-between gap-2 py-1"}>
                    <span className={"my-auto line-clamp-1"}>{user.name}</span>
                    <AlertDialog>
                      <AlertDialogTrigger>
                        <TrashIcon className={"size-5 object-contain"} />
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{props.alertDeleteUserTitle}</AlertDialogTitle>
                          <AlertDialogDescription>{props.alertDeleteUserDescription}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeUserAction(user.name)}>
                            {t("common.confirm")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Punchout Config URLs */}
        <div className={"flex flex-col gap-2 rounded-sm border border-gray-300 bg-gray-200 p-2"}>
          <span className={"text-xl font-semibold"}>{props.labelPunchoutUrlsTitle}</span>
          <p>{props.labelPunchoutUrlsDescription}</p>
          {punchoutConfigUrls.map((url) => (
            <div key={url.label} className={"flex flex-col"}>
              <span className={"text-xl font-semibold"}>{url.label}</span>
              <Textarea
                className={"bg-gray-50"}
                value={url.value}
                readOnly={true}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          ))}
          <div className={"grid gap-2 max-lg:grid-rows-4 lg:grid-cols-4"}>
            <Input
              type="text"
              placeholder={"<DOMAIN>"}
              value={punchoutConfigDomain}
              onChange={(e) => setPunchoutConfigDomain(e.target.value)}
            />
            <Input
              type="text"
              placeholder={"<USERNAME>"}
              value={punchoutConfigUser}
              onChange={(e) => setPunchoutConfigUser(e.target.value)}
            />
            <Input
              type="text"
              placeholder={"<PASSWORD>"}
              value={punchoutConfigPass}
              onChange={(e) => setPunchoutConfigPass(e.target.value)}
            />
            <Input
              type="text"
              placeholder={"<HOOK_URL>"}
              value={punchoutConfigHook}
              onChange={(e) => setPunchoutConfigHook(e.target.value)}
            />
          </div>
        </div>
      </div>
    </>
  )
}
